import BedtimeOutlinedIcon from '@mui/icons-material/BedtimeOutlined';
import LunchDiningOutlinedIcon from '@mui/icons-material/LunchDiningOutlined';
import DirectionsRunOutlinedIcon from '@mui/icons-material/DirectionsRunOutlined';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import WcOutlinedIcon from '@mui/icons-material/WcOutlined';
import MoodOutlinedIcon from '@mui/icons-material/MoodOutlined';
import MedicalInformationOutlinedIcon from '@mui/icons-material/MedicalInformationOutlined';
import StarOutlinedIcon from '@mui/icons-material/StarOutlined';
import SvgIcon from '@mui/material/SvgIcon';

const MedicationCapsuleIcon = (props) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path
      d="M9.5 4.75a4.75 4.75 0 0 1 6.72 6.72l-6.25 6.25a4.75 4.75 0 1 1-6.72-6.72l6.25-6.25Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinejoin="round"
    />
    <path
      d="M8.05 8.9 15.1 15.95"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
    />
  </SvgIcon>
);

const createPalette = ({
  softBg,
  strongBg = softBg,
  text,
  border,
  dot = border,
}) => ({
  bg: softBg,
  softBg,
  strongBg,
  text,
  ink: text,
  border,
  dot,
});

export const CATEGORY_PALETTES = {
  medication: createPalette({
    softBg: '#F4FAF7',
    strongBg: '#C9E4D8',
    text: '#2F5A49',
    border: '#CFE7DC',
    dot: '#2F5A49',
  }),
  sleep: createPalette({
    softBg: '#F2F6FC',
    strongBg: '#D0DFF2',
    text: '#334465',
    border: '#C7D8EF',
    dot: '#334465',
  }),
  food: createPalette({
    softBg: '#FCF7F3',
    strongBg: '#F4D9C8',
    text: '#6B3A24',
    border: '#E6CDBA',
    dot: '#6B3A24',
  }),
  activity: createPalette({
    softBg: '#F5F0FA',
    strongBg: '#DECCEE',
    text: '#4A2E5E',
    border: '#D7C6EA',
    dot: '#DECCEE',
  }),
  behavior: createPalette({
    softBg: '#FCF5F7',
    strongBg: '#E8A8C0',
    text: '#6B1A38',
    border: '#E4B3C7',
    dot: '#6B1A38',
  }),
  mood: createPalette({
    softBg: '#FDF6FD',
    strongBg: '#EEC0F0',
    text: '#6B1F7A',
    border: '#E2C0EC',
    dot: '#6B1F7A',
  }),
  bathroom: createPalette({
    softBg: '#F6F8E8',
    strongBg: '#E4EAB8',
    text: '#4C521F',
    border: '#DCE5B6',
    dot: '#4C521F',
  }),
  health: createPalette({
    softBg: '#EEF9F8',
    strongBg: '#A8E4DC',
    text: '#1A5C54',
    border: '#B5E4DE',
    dot: '#1A5C54',
  }),
  medication_change: createPalette({
    softBg: '#F5F0FC',
    strongBg: '#E0D4F4',
    text: '#5B3D87',
    border: '#D9CFEE',
    dot: '#5B3D87',
  }),
  milestone: createPalette({
    softBg: '#FDFAEA',
    strongBg: '#EEE4A8',
    text: '#6B5A1F',
    border: '#E5E0A9',
    dot: '#6B5A1F',
  }),
  log: createPalette({
    softBg: '#F4F6F4',
    strongBg: '#E0E8E4',
    text: '#4A5954',
    border: '#D8E2DD',
    dot: '#4A5954',
  }),
  importantMoment: createPalette({
    softBg: '#FEFCF0',
    strongBg: '#F8E88C',
    text: '#7A5000',
    border: '#E8DE93',
    dot: '#7A5000',
  }),
};

const CAREGIVER_PALETTES = [
  { bg: '#E3EAF0', ink: '#3A4E66' },
  { bg: '#F3E4D0', ink: '#6B4A24' },
  { bg: '#E8EDC8', ink: '#4C521F' },
  { bg: '#D6E8E1', ink: '#2F5A49' },
  { bg: '#E3D5EC', ink: '#4A2E5E' },
  { bg: '#F2D4C9', ink: '#8B4A36' },
];

const hashString = (value = '') => {
  const input = String(value || '').trim();
  if (!input) {
    return 0;
  }

  let hash = 0;
  for (let index = 0; index < input.length; index += 1) {
    hash = ((hash << 5) - hash) + input.charCodeAt(index);
    hash |= 0;
  }

  return Math.abs(hash);
};

export const getCaregiverPalette = (userId = '') => {
  const paletteIndex = CAREGIVER_PALETTES.length > 0
    ? hashString(userId) % CAREGIVER_PALETTES.length
    : 0;

  return CAREGIVER_PALETTES[paletteIndex] || CAREGIVER_PALETTES[0];
};

export const LOG_TYPES = {
  sleep: {
    id: 'sleep',
    category: 'sleep',
    type: 'sleep',
    timelineType: 'sleep',
    displayLabel: 'Sleep',
    filterLabel: 'Sleep',
    trackLabel: 'Sleep',
    icon: null,
    iconComponent: BedtimeOutlinedIcon,
    palette: CATEGORY_PALETTES.sleep,
    trackChip: true,
    trackOrder: 2,
    filterGroup: 'entryType',
  },
  food: {
    id: 'food',
    category: 'food',
    type: 'food',
    timelineType: 'food',
    displayLabel: 'Food',
    filterLabel: 'Food',
    trackLabel: 'Food',
    icon: null,
    iconComponent: LunchDiningOutlinedIcon,
    palette: CATEGORY_PALETTES.food,
    trackChip: true,
    trackOrder: 3,
    filterGroup: 'entryType',
  },
  medication: {
    id: 'medication',
    category: 'medication',
    type: 'medication',
    timelineType: 'medication',
    displayLabel: 'Meds',
    filterLabel: 'Meds',
    trackLabel: 'Meds',
    icon: null,
    iconComponent: MedicationCapsuleIcon,
    palette: CATEGORY_PALETTES.medication,
    trackChip: true,
    trackOrder: 1,
    filterGroup: 'entryType',
  },
  medication_change: {
    id: 'medication_change',
    category: 'medication_change',
    type: 'medication_change',
    timelineType: 'medication_change',
    displayLabel: 'Medication change',
    filterLabel: 'Medication change',
    trackLabel: 'Medication change',
    icon: null,
    iconComponent: MedicalInformationOutlinedIcon,
    palette: CATEGORY_PALETTES.medication_change,
    trackChip: false,
    filterGroup: 'entryType',
  },
  bathroom: {
    id: 'bathroom',
    category: 'bathroom',
    type: 'bathroom',
    timelineType: 'bathroom',
    displayLabel: 'Toilet',
    filterLabel: 'Toilet',
    trackLabel: 'Toilet',
    icon: null,
    iconComponent: WcOutlinedIcon,
    palette: CATEGORY_PALETTES.bathroom,
    trackChip: true,
    trackOrder: 4,
    filterGroup: 'entryType',
  },
  activity: {
    id: 'activity',
    category: 'activity',
    type: 'activity',
    timelineType: 'activity',
    displayLabel: 'Activity',
    filterLabel: 'Activity',
    trackLabel: 'Activity',
    icon: null,
    iconComponent: DirectionsRunOutlinedIcon,
    palette: CATEGORY_PALETTES.activity,
    trackChip: true,
    trackOrder: 5,
    filterGroup: 'entryType',
  },
  health: {
    id: 'health',
    category: 'health',
    type: 'health',
    timelineType: 'health',
    displayLabel: 'Health',
    filterLabel: 'Health',
    trackLabel: 'Health',
    icon: null,
    iconComponent: MedicalInformationOutlinedIcon,
    palette: CATEGORY_PALETTES.health,
    trackChip: false,
    filterGroup: 'entryType',
  },
  behavior: {
    id: 'behavior',
    category: 'behavior',
    type: 'behavior',
    timelineType: 'behavior',
    displayLabel: 'Behavior',
    filterLabel: 'Behavior',
    trackLabel: 'Behavior',
    icon: null,
    iconComponent: PsychologyOutlinedIcon,
    palette: CATEGORY_PALETTES.behavior,
    trackChip: true,
    trackOrder: 6,
    filterGroup: 'flagged',
  },
  mood: {
    id: 'mood',
    category: 'mood',
    type: 'mood',
    timelineType: 'mood',
    displayLabel: 'Mood',
    filterLabel: 'Mood',
    trackLabel: 'Mood',
    icon: null,
    iconComponent: MoodOutlinedIcon,
    palette: CATEGORY_PALETTES.mood,
    trackChip: false,
    filterGroup: 'flagged',
  },
  milestone: {
    id: 'milestone',
    category: 'milestone',
    type: 'milestone',
    timelineType: 'milestone',
    displayLabel: 'Milestone',
    filterLabel: 'Milestone',
    trackLabel: 'Milestone',
    icon: null,
    iconComponent: StarOutlinedIcon,
    palette: CATEGORY_PALETTES.milestone,
    trackChip: false,
    filterGroup: 'flagged',
  },
  log: {
    id: 'log',
    category: 'dailyLog',
    type: 'dailyLog',
    timelineType: 'dailyLog',
    displayLabel: 'Daily Log',
    filterLabel: 'Daily Log',
    trackLabel: 'Daily Log',
    icon: '📝',
    iconComponent: null,
    palette: CATEGORY_PALETTES.log,
    trackChip: false,
    filterGroup: 'entryType',
  },
};

export const TAG_TYPES = {
  meltdown: {
    id: 'meltdown',
    label: 'Meltdown',
    icon: null,
    iconComponent: PsychologyOutlinedIcon,
    category: 'behavior',
    placeholder: 'What triggered it? How long did it last?',
  },
  anxiety: {
    id: 'anxiety',
    label: 'Anxiety',
    icon: null,
    iconComponent: MoodOutlinedIcon,
    category: 'mood',
    placeholder: 'What caused it? How did they respond?',
  },
  sensory: {
    id: 'sensory',
    label: 'Sensory',
    icon: null,
    iconComponent: MedicalInformationOutlinedIcon,
    category: 'health',
    placeholder: 'What triggered it? Sounds, lights, textures, crowds?',
  },
};

export const SPECIAL_FILTER_TYPES = {
  importantMoment: {
    id: 'importantMoment',
    value: 'importantMoment',
    label: 'Important Moments',
    titlePrefix: 'Important Moment',
    icon: null,
    iconComponent: StarOutlinedIcon,
    color: CATEGORY_PALETTES.importantMoment,
  },
};

export const CATEGORY_COLORS = Object.entries(LOG_TYPES).reduce((acc, [typeKey, type]) => {
  acc[typeKey] = type.palette;
  acc[type.category] = type.palette;
  return acc;
}, {
  importantMoment: SPECIAL_FILTER_TYPES.importantMoment.color,
});

export const QUICK_TAG_CATEGORY_MAP = Object.entries(TAG_TYPES).reduce((acc, [tagId, tagType]) => {
  acc[tagId] = tagType.category;
  return acc;
}, {});

export const QUICK_TAG_PLACEHOLDERS = Object.entries(TAG_TYPES).reduce((acc, [tagId, tagType]) => {
  acc[tagId] = tagType.placeholder;
  return acc;
}, {});

export const QUICK_TAG_GROUPS = [
  {
    id: 'quick-context',
    label: 'Quick context',
    items: ['meltdown', 'anxiety', 'sensory'].map((tagId) => ({
      key: tagId,
      label: TAG_TYPES[tagId].label,
      icon: TAG_TYPES[tagId].icon,
      iconComponent: TAG_TYPES[tagId].iconComponent || null,
    })),
  },
];

export const CATEGORY_TILE_CONFIGS = [
  {
    key: 'meds',
    label: LOG_TYPES.medication.displayLabel,
    icon: LOG_TYPES.medication.iconComponent,
    category: 'medication',
    bg: LOG_TYPES.medication.palette.softBg,
    chipBg: LOG_TYPES.medication.palette.strongBg,
    ink: LOG_TYPES.medication.palette.text,
  },
  {
    key: 'sleep',
    label: LOG_TYPES.sleep.displayLabel,
    icon: LOG_TYPES.sleep.iconComponent || BedtimeOutlinedIcon,
    category: 'sleep',
    bg: LOG_TYPES.sleep.palette.softBg,
    chipBg: LOG_TYPES.sleep.palette.strongBg,
    ink: LOG_TYPES.sleep.palette.text,
  },
  {
    key: 'food',
    label: LOG_TYPES.food.displayLabel,
    icon: LOG_TYPES.food.iconComponent || LunchDiningOutlinedIcon,
    category: 'food',
    bg: LOG_TYPES.food.palette.softBg,
    chipBg: LOG_TYPES.food.palette.strongBg,
    ink: LOG_TYPES.food.palette.text,
  },
  {
    key: 'activity',
    label: LOG_TYPES.activity.displayLabel,
    icon: LOG_TYPES.activity.iconComponent || DirectionsRunOutlinedIcon,
    category: 'activity',
    bg: LOG_TYPES.activity.palette.softBg,
    chipBg: LOG_TYPES.activity.palette.strongBg,
    ink: LOG_TYPES.activity.palette.text,
  },
  {
    key: 'behavior',
    label: LOG_TYPES.behavior.displayLabel,
    icon: LOG_TYPES.behavior.iconComponent || PsychologyOutlinedIcon,
    category: 'incident',
    bg: LOG_TYPES.behavior.palette.softBg,
    chipBg: LOG_TYPES.behavior.palette.strongBg,
    ink: LOG_TYPES.behavior.palette.text,
  },
  {
    key: 'toileting',
    label: LOG_TYPES.bathroom.displayLabel,
    icon: LOG_TYPES.bathroom.iconComponent || WcOutlinedIcon,
    category: 'bathroom',
    bg: LOG_TYPES.bathroom.palette.softBg,
    chipBg: LOG_TYPES.bathroom.palette.strongBg,
    ink: LOG_TYPES.bathroom.palette.text,
  },
];

export const TRACK_LOG_TYPES = Object.values(LOG_TYPES)
  .filter((type) => type.trackChip)
  .sort((a, b) => (a.trackOrder || 999) - (b.trackOrder || 999));

export const CORE_ENTRY_ACTIONS = [
  {
    key: 'meds',
    type: 'medication',
    label: LOG_TYPES.medication.displayLabel,
    icon: LOG_TYPES.medication.iconComponent ? null : LOG_TYPES.medication.icon,
    iconComponent: LOG_TYPES.medication.iconComponent || null,
    color: LOG_TYPES.medication.palette.dot,
  },
  {
    key: 'sleep',
    type: 'sleep',
    label: LOG_TYPES.sleep.displayLabel,
    icon: LOG_TYPES.sleep.iconComponent ? null : LOG_TYPES.sleep.icon,
    iconComponent: LOG_TYPES.sleep.iconComponent || null,
    color: LOG_TYPES.sleep.palette.dot,
  },
  {
    key: 'food',
    type: 'food',
    label: LOG_TYPES.food.displayLabel,
    icon: LOG_TYPES.food.iconComponent ? null : LOG_TYPES.food.icon,
    iconComponent: LOG_TYPES.food.iconComponent || null,
    color: LOG_TYPES.food.palette.dot,
  },
  {
    key: 'toilet',
    type: 'bathroom',
    label: LOG_TYPES.bathroom.displayLabel,
    icon: LOG_TYPES.bathroom.iconComponent ? null : LOG_TYPES.bathroom.icon,
    iconComponent: LOG_TYPES.bathroom.iconComponent || null,
    color: LOG_TYPES.bathroom.palette.dot,
  },
  {
    key: 'activity',
    type: 'activity',
    label: LOG_TYPES.activity.displayLabel,
    icon: LOG_TYPES.activity.iconComponent ? null : LOG_TYPES.activity.icon,
    iconComponent: LOG_TYPES.activity.iconComponent || null,
    color: LOG_TYPES.activity.palette.dot,
  },
  {
    key: 'behavior',
    type: 'behavior',
    label: LOG_TYPES.behavior.displayLabel,
    icon: LOG_TYPES.behavior.iconComponent ? null : LOG_TYPES.behavior.icon,
    iconComponent: LOG_TYPES.behavior.iconComponent || null,
    color: LOG_TYPES.behavior.palette.dot,
  },
];

export const TIMELINE_FILTER_SECTIONS = {
  allEntries: {
    value: null,
    label: 'All entries',
  },
  entryType: {
    label: 'Entry type',
    items: TRACK_LOG_TYPES.map((type) => ({
      value: type.category,
      label: type.filterLabel,
      icon: type.iconComponent ? null : type.icon,
      iconComponent: type.iconComponent || null,
    })),
  },
  flaggedAs: {
    label: 'Flagged as',
    items: [
      SPECIAL_FILTER_TYPES.importantMoment,
      LOG_TYPES.behavior,
      LOG_TYPES.milestone,
      LOG_TYPES.mood,
    ].map((type) => ({
      value: type.value || type.category,
      label: type.label || type.filterLabel,
      icon: type.iconComponent ? null : type.icon,
      iconComponent: type.iconComponent || null,
    })),
  },
};

export const getLogTypeById = (id) => LOG_TYPES[id] || LOG_TYPES.log;

export const getLogTypeByCategory = (category) => {
  if (!category) return LOG_TYPES.log;
  if (LOG_TYPES[category]) return LOG_TYPES[category];

  if (category === 'toileting') {
    return LOG_TYPES.bathroom;
  }

  if (category === 'dailyLog' || category === 'log') {
    return LOG_TYPES.log;
  }

  if (category === 'medication_change' || category === 'medication-change') {
    return LOG_TYPES.medication_change;
  }

  return LOG_TYPES.log;
};

const BATHROOM_KEYWORDS = [
  'bathroom',
  'toileting',
  'toilet',
  'urination',
  'urinated',
  'pee',
  'peeing',
  'bowel movement',
  'bowel',
  'poop',
  'stool',
  'diaper',
  'accident',
];

const inferBathroomCategory = (entry = {}) => {
  const haystack = [
    entry.category,
    entry.parsedSourceCategory,
    entry.source?.parsedSourceCategory,
    entry.timelineType,
    entry.type,
    entry.titlePrefix,
    entry.title,
    entry.label,
    entry.displayLabel,
    entry.text,
    entry.content,
    entry.summary,
    entry.notes,
    entry.bathroomDetails?.type,
    entry.bathroomDetails?.location,
    ...(entry.tags || []),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return BATHROOM_KEYWORDS.some((keyword) => haystack.includes(keyword));
};

const BEHAVIOR_IDENTIFIERS = new Set(['behavior', 'behavioral']);
const BEHAVIOR_TEXT_PATTERN = /(?:cranky|upset|meltdown|tantrum|trouble focusing|difficulty focusing|aggress(?:ive|ion)?|refus(?:ed|al)|frustrat(?:ed|ion)?|irritable|needed a break|quiet space|hit|pushed|kicked|screamed|yelled|distract(?:ed|ion)?|eloped?|elopement)/i;

const hasBehaviorTextSignal = (entry = {}) => {
  const haystack = [
    entry.description,
    entry.summary,
    entry.text,
    entry.content,
    entry.notes,
    entry.note,
    entry.triggerSummary,
    entry.title,
    entry.label,
    entry.categoryLabel,
    entry.titlePrefix,
    entry.incidentData?.description,
    entry.incidentData?.notes,
    entry.incidentData?.triggerSummary,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return BEHAVIOR_TEXT_PATTERN.test(haystack);
};

const hasAdaptiveQuickLogLens = (entry = {}) => Boolean(
  entry?.adaptiveQuickLogLabel ||
  entry?.adaptiveQuickLogLens ||
  entry?.incidentData?.adaptiveQuickLogLabel ||
  entry?.incidentData?.adaptiveQuickLogLens
);

export const isBehaviorIncidentEntry = (entry = {}) => Boolean(
  (
    entry?.logCategory && String(entry.logCategory).toLowerCase() !== 'behavior' && !hasAdaptiveQuickLogLens(entry)
      ? false
      : (
        hasAdaptiveQuickLogLens(entry) ||
        entry?.incidentStyle ||
        entry?.entryType === 'incident' ||
        (
          entry?.incidentData &&
          typeof entry.incidentData === 'object' &&
          (
            entry.incidentData.description
            || entry.incidentData.notes
            || entry.incidentData.triggerSummary
            || entry.incidentData.severity
            || entry.incidentData.severityLabel
            || entry.incidentData.remedy
            || entry.incidentData.contextSnapshot
            || entry.incidentData.customIncidentName
            || entry.incidentData.incidentType
          )
        ) ||
        BEHAVIOR_IDENTIFIERS.has(String(entry?.category || '').toLowerCase()) ||
        BEHAVIOR_IDENTIFIERS.has(String(entry?.timelineType || '').toLowerCase()) ||
        BEHAVIOR_IDENTIFIERS.has(String(entry?.type || '').toLowerCase()) ||
        BEHAVIOR_IDENTIFIERS.has(String(entry?.categoryLabel || '').toLowerCase()) ||
        BEHAVIOR_IDENTIFIERS.has(String(entry?.titlePrefix || '').toLowerCase()) ||
        BEHAVIOR_IDENTIFIERS.has(String(entry?.incidentCategoryId || '').toLowerCase()) ||
        BEHAVIOR_IDENTIFIERS.has(String(entry?.parsedSourceCategory || '').toLowerCase()) ||
        BEHAVIOR_IDENTIFIERS.has(String(entry?.source?.parsedSourceCategory || '').toLowerCase()) ||
        BEHAVIOR_IDENTIFIERS.has(String(entry?.label || '').toLowerCase()) ||
        hasBehaviorTextSignal(entry)
      )
  )
);

const normalizeDisplayCategory = (entry = {}) => {
  const candidates = [
    entry?.category,
    entry?.type,
    entry?.incidentCategoryId,
    entry?.categoryId,
    entry?.parsedSourceCategory,
    entry?.source?.parsedSourceCategory,
  ];

  for (const candidate of candidates) {
    const normalized = String(candidate || '').trim().toLowerCase();
    if (!normalized) {
      continue;
    }

    if (normalized === 'dailylog' || normalized === 'log') {
      continue;
    }

    return normalized;
  }

  return 'log';
};

const getAdaptiveBehaviorDisplayLabel = (entry = {}) => String(
  entry.adaptiveQuickLogLabel
  || entry.incidentData?.adaptiveQuickLogLabel
  || entry.incidentCategoryLabel
  || ''
).trim();

export const getLogTypeByEntry = (entry = {}) => {
  if (!entry || typeof entry !== 'object') {
    return LOG_TYPES.log;
  }

  const directCategory = normalizeDisplayCategory(entry);
  const directType = getLogTypeByCategory(directCategory);

  if (directCategory && (directType !== LOG_TYPES.log || directCategory === 'log')) {
    return directType;
  }

  if (isBehaviorIncidentEntry(entry)) {
    return LOG_TYPES.behavior;
  }

  if (inferBathroomCategory(entry)) {
    return LOG_TYPES.bathroom;
  }

  return directType;
};

export const getTagTypeById = (id) => TAG_TYPES[id] || null;

export const getTrackLogTypes = () => TRACK_LOG_TYPES;

export const getQuickTagGroups = () => QUICK_TAG_GROUPS;

export const getQuickTagPlaceholder = (tagId) => QUICK_TAG_PLACEHOLDERS[tagId] || "Just write what happened — we'll figure out the rest.";

export const getQuickTagCategory = (tagId) => QUICK_TAG_CATEGORY_MAP[tagId] || 'log';

export const getTimelineFilterSections = () => TIMELINE_FILTER_SECTIONS;

export const getTimelineMetaForCategory = (category, { importantMoment = false } = {}) => {
  if (importantMoment) {
    return {
      type: 'importantMoment',
      timelineType: 'importantMoment',
      titlePrefix: SPECIAL_FILTER_TYPES.importantMoment.titlePrefix,
      label: SPECIAL_FILTER_TYPES.importantMoment.titlePrefix,
      color: SPECIAL_FILTER_TYPES.importantMoment.color.dot,
      icon: SPECIAL_FILTER_TYPES.importantMoment.icon,
      iconComponent: SPECIAL_FILTER_TYPES.importantMoment.iconComponent || null,
    };
  }

  const logType = getLogTypeByCategory(category);
  return {
    type: logType.type,
    timelineType: logType.timelineType,
    titlePrefix: logType.displayLabel,
    label: logType.displayLabel,
    color: logType.palette.dot,
    icon: logType.iconComponent ? null : logType.icon,
    iconComponent: logType.iconComponent || null,
  };
};

export const getCanonicalEntryDisplayInfo = (entry = {}) => {
  const categoryType = getLogTypeByEntry(entry);
  const displayCategory = normalizeDisplayCategory(entry);
  const categoryMeta = getLogTypeByCategory(categoryType.category || displayCategory);
  const isBehaviorIncident = isBehaviorIncidentEntry(entry);

  return {
    category: categoryType.category || displayCategory || 'log',
    label: isBehaviorIncident
      ? (getAdaptiveBehaviorDisplayLabel(entry) || LOG_TYPES.behavior.displayLabel)
      : (categoryMeta.trackLabel || categoryMeta.displayLabel || categoryMeta.filterLabel || 'Log'),
    icon: isBehaviorIncident
      ? (LOG_TYPES.behavior.iconComponent ? null : LOG_TYPES.behavior.icon)
      : (categoryMeta.iconComponent ? null : (categoryMeta.icon || '•')),
    iconComponent: isBehaviorIncident
      ? LOG_TYPES.behavior.iconComponent
      : (categoryMeta.iconComponent || null),
    palette: isBehaviorIncident
      ? LOG_TYPES.behavior.palette
      : (categoryMeta.palette || null),
    titlePrefix: isBehaviorIncident
      ? (getAdaptiveBehaviorDisplayLabel(entry) || LOG_TYPES.behavior.displayLabel)
      : (categoryMeta.trackLabel || categoryMeta.displayLabel || 'Log'),
  };
};
